import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wand2, Repeat, Loader2 } from "lucide-react";

/**
 * AdminBulkCategoryTools
 * Admin-only utilities that run large category-cleanup operations against the
 * live businesses collection.
 *   • Bulk Recategorize  — reassign businesses whose *name* matches a keyword
 *     list to a target category. Used to fix Google-Places-imported rows
 *     landing under "Other" when they're clearly medical/health/etc.
 *   • Rename Category    — reassign every business in one category to another.
 *     Used when a category is renamed (e.g. "Books & Media" → "Media and
 *     Entertainment") so existing rows follow.
 *
 * Both operations POST to admin endpoints in server.py.
 */
export default function AdminBulkCategoryTools() {
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);

  // Bulk recategorize state
  const [keywords, setKeywords] = useState("health, counseling, medicine, doctor");
  const [toCategory, setToCategory] = useState("Medical Services");
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Rename category state
  const [fromCategory, setFromCategory] = useState("");
  const [renameTo, setRenameTo] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [renameResult, setRenameResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/categories`);
        setCategories(r.data?.categories || []);
      } catch {
        toast.error("Could not load categories");
      } finally {
        setLoadingCats(false);
      }
    })();
  }, []);

  const runBulkRecategorize = async () => {
    const parsedKeywords = keywords
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    if (parsedKeywords.length === 0) {
      toast.error("Enter at least one keyword");
      return;
    }
    if (!toCategory) {
      toast.error("Select a target category");
      return;
    }
    if (
      !window.confirm(
        `Recategorize ALL businesses whose name contains any of: ${parsedKeywords.join(", ")}\n\n→ Move them into "${toCategory}"\n\nThis cannot be undone. Continue?`
      )
    )
      return;

    setRunning(true);
    setLastResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/businesses/bulk-recategorize`,
        { keywords: parsedKeywords, to_category: toCategory, dry_run: false },
        { withCredentials: true }
      );
      setLastResult(r.data);
      toast.success(
        `${r.data.modified_count} business${r.data.modified_count === 1 ? "" : "es"} moved to "${toCategory}"`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk recategorize failed");
    } finally {
      setRunning(false);
    }
  };

  const runRenameCategory = async () => {
    if (!fromCategory || !renameTo) {
      toast.error("Pick a source and target category");
      return;
    }
    if (fromCategory === renameTo) {
      toast.error("Source and target must differ");
      return;
    }
    if (
      !window.confirm(
        `Reassign EVERY business currently in "${fromCategory}"\n\n→ "${renameTo}"\n\nContinue?`
      )
    )
      return;

    setRenaming(true);
    setRenameResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/businesses/rename-category`,
        { from_category: fromCategory, to_category: renameTo },
        { withCredentials: true }
      );
      setRenameResult(r.data);
      toast.success(
        `${r.data.modified_count} business${r.data.modified_count === 1 ? "" : "es"} moved from "${fromCategory}" → "${renameTo}"`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename category failed");
    } finally {
      setRenaming(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2" data-testid="admin-bulk-category-tools">
      {/* Bulk Recategorize */}
      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-tbc-navy/5 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-4 h-4 text-tbc-navy" />
          </div>
          <div>
            <h3 className="font-heading text-tbc-navy">Bulk Recategorize</h3>
            <p className="text-xs text-gray-500 font-body mt-0.5">
              Move businesses whose <em>name</em> contains any of the keywords into the target category.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-600">Keywords (comma-separated)</Label>
            <Input
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="health, counseling, medicine, doctor"
              data-testid="bulk-recat-keywords"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Target category</Label>
            <Select value={toCategory} onValueChange={setToCategory}>
              <SelectTrigger data-testid="bulk-recat-target">
                <SelectValue placeholder={loadingCats ? "Loading…" : "Pick a category"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={runBulkRecategorize}
            disabled={running}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white w-full"
            data-testid="bulk-recat-run"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…
              </>
            ) : (
              "Run Bulk Recategorize"
            )}
          </Button>

          {lastResult && (
            <div
              className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 font-body"
              data-testid="bulk-recat-result"
            >
              <p className="font-semibold">
                {lastResult.modified_count} moved · {lastResult.matched_count} matched
              </p>
              {lastResult.matched?.length > 0 && (
                <ul className="mt-1.5 max-h-40 overflow-y-auto space-y-0.5">
                  {lastResult.matched.slice(0, 25).map((b) => (
                    <li key={b.business_id || b.name} className="truncate">
                      • {b.name}
                    </li>
                  ))}
                  {lastResult.matched.length > 25 && (
                    <li className="text-emerald-700">
                      …and {lastResult.matched.length - 25} more
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rename Category */}
      <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-tbc-gold/10 flex items-center justify-center flex-shrink-0">
            <Repeat className="w-4 h-4 text-tbc-gold" />
          </div>
          <div>
            <h3 className="font-heading text-tbc-navy">Rename / Merge Category</h3>
            <p className="text-xs text-gray-500 font-body mt-0.5">
              Reassign every business in one category to another. Use after
              renaming a category so existing rows follow.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-600">From category</Label>
            <Select value={fromCategory} onValueChange={setFromCategory}>
              <SelectTrigger data-testid="rename-cat-from">
                <SelectValue placeholder={loadingCats ? "Loading…" : "Pick source"} />
              </SelectTrigger>
              <SelectContent>
                {/* Include the legacy label so admins can migrate legacy rows */}
                <SelectItem value="Books & Media">Books &amp; Media (legacy)</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-600">To category</Label>
            <Select value={renameTo} onValueChange={setRenameTo}>
              <SelectTrigger data-testid="rename-cat-to">
                <SelectValue placeholder={loadingCats ? "Loading…" : "Pick target"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={runRenameCategory}
            disabled={renaming}
            className="bg-tbc-navy hover:bg-tbc-navy/90 text-white w-full"
            data-testid="rename-cat-run"
          >
            {renaming ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…
              </>
            ) : (
              "Rename Category"
            )}
          </Button>

          {renameResult && (
            <div
              className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 font-body"
              data-testid="rename-cat-result"
            >
              <p className="font-semibold">
                {renameResult.modified_count} moved from &ldquo;{renameResult.from_category}&rdquo; →
                &ldquo;{renameResult.to_category}&rdquo;
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
